import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import _ from "lodash";
import { markGPReportService, triggerGPReportDownload } from "../../../services/gpReport";
import { buildShareUrl, listSecureFiles, retryOutboundLetterSecureFile } from "../../../services/secureFiles";
import {
  getOutboundLetterLinkState,
  getVerifiedOutboundLetters,
  resolveOutboundLetterLink,
  OUTBOUND_LETTER_LINK_STATE,
  OUTBOUND_LETTER_SECURE_FILE_STATUS,
} from "../../../utils/gpReport";
import { ADMIN_ROLE } from "../../../constants";
import { getToggleEnabled, TOGGLES } from "../../../toggles";

import { displayDateTime } from "../../../utils/dateDisplay";
// `time` is the PMS event time (ISO string), shown in the tenant zone with
// its abbreviation like every other time on screen - two coordinators in
// different states must read the same value.
const formatLocalTime = (value) => displayDateTime(value, "DD MMM YYYY HH:mm");

const PANEL_STYLE = {
  width: "100%",
  border: "1px solid #ededed",
  borderRadius: 4,
  overflow: "hidden",
};

const HEADER_STYLE = {
  padding: "8px 14px",
  borderBottom: "1px solid #ededed",
  background: "#fafafa",
};

// 4px transparent left border on every row so the layout doesn't shift when a
// row becomes marked (the marked-row override below replaces this with a green
// accent of the same width).
const ROW_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: 16,
  padding: "10px 14px",
  borderBottom: "1px solid #f0f0f0",
  borderLeft: "4px solid transparent",
  background: "#fff",
  transition: "background-color 120ms ease-in-out, border-left-color 120ms ease-in-out",
};

// Marked row styling — pairs a subtle green wash with a vibrant left-border
// accent so the chosen letter is identifiable at a glance, even before the
// reader looks at the button column.
const MARKED_ROW_OVERRIDE = {
  background: "#f0fbf4",
  borderLeftColor: "#48c774", // matches Bulma `is-success`
};

const TIME_COL_STYLE = {
  flex: "0 0 200px",
  color: "#4a4a4a",
};

const NAME_COL_STYLE = {
  flex: "1 1 auto",
  minWidth: 0, // critical for ellipsis inside flex children
  // Zero preferred width: the panel sits in an auto-layout table cell, which
  // otherwise widens to the full nowrap name and pushes the action buttons out
  // of view; the column still grows to fill the space left by the buttons.
  width: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const ACTIONS_COL_STYLE = {
  flex: "0 0 auto",
  marginLeft: "auto",
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const MARK_BUTTON_BASE_STYLE = {
  minWidth: 144, // keep button width identical between marked / unmarked so the
  // download button doesn't jump horizontally when a row's state flips.
  justifyContent: "center",
};

// Keeps "Copy secure link" and "Copied" the same width so the row's other
// buttons don't move while the confirmation shows.
const COPY_BUTTON_STYLE = {
  minWidth: 128,
  justifyContent: "center",
};

const LINK_STATUS_TEXT_STYLE = {
  whiteSpace: "nowrap",
};

const COPIED_DURATION_MS = 2000;
const RETRY_ERROR_MESSAGE = "Could not create the secure link. Please try again.";
const LOAD_ERROR_MESSAGE = "Could not load secure links.";

// Stable identifier for an outbound-letter entry — `pmsFileId` is the PMS UUID
// when present (the only kind that can be marked); fall back to `id` (legacy
// rows that pre-date PMS) so the row still has a unique React key.
const getReportKey = (report) => report?.pmsFileId || report?.id || "";

const GPReportListPanel = ({ ca }) => {
  const [downloadingKey, setDownloadingKey] = useState(null);
  const [markingKey, setMarkingKey] = useState(null);
  const [panelError, setPanelError] = useState(null);

  // Secure File links (SE-646): same audience as the Secure File tab — the
  // toggle that shows the tab, and the roles the secure-files API accepts.
  const currentUser = useSelector((state) => state.user?.currentUser);
  const canUseSecureLinks = getToggleEnabled(TOGGLES.SECURE_FILES) && ADMIN_ROLE.includes(currentUser?.role || "");
  const caId = _.get(ca, "id", "") || "";
  const [secureFiles, setSecureFiles] = useState({ caId: "", signature: "", byToken: null });
  // pmsFileIds with a Retry in flight; each row retries independently.
  const [retryingKeys, setRetryingKeys] = useState({});
  const [copiedKey, setCopiedKey] = useState(null);
  // Results of a successful Retry, keyed by `${caId}:${pmsFileId}`, so the row
  // updates before (or without) the parent's `ca` being refreshed.
  const [linkOverrides, setLinkOverrides] = useState({});
  const copiedTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(copiedTimerRef.current), []);

  // Currently-marked entry. Initialised from `ca.gp_report.pmsFileId` (set
  // either by the legacy webhook flow or by a previous "Mark" click) and
  // updated optimistically when the user marks a different row. The local
  // override is rebased whenever the parent's `ca` prop changes (e.g. from a
  // Firestore snapshot listener).
  const remoteMarkedPmsFileId = _.get(ca, "gp_report.pmsFileId", "") || "";
  const [markedPmsFileId, setMarkedPmsFileId] = useState(remoteMarkedPmsFileId);
  useEffect(() => {
    setMarkedPmsFileId(remoteMarkedPmsFileId);
  }, [remoteMarkedPmsFileId]);

  // Prefer the PMS-driven history (verified entries only — letters PMS has
  // archived are filtered out so the panel never shows files that no longer
  // exist on the source system). Fall back to the legacy single `gp_report`
  // so older docs still render in the panel.
  const verifiedReports = getVerifiedOutboundLetters(ca);
  const reports = !_.isEmpty(verifiedReports)
    ? verifiedReports
    : _.has(ca, "gp_report")
    ? [_.get(ca, "gp_report")]
    : [];

  const sortedReports = _.orderBy(reports, [(r) => r.time || r.appointment_date || ""], ["desc"]);

  // The row's link: the backend's fields, unless a Retry from this panel has
  // just fixed a link the (possibly stale) `ca` still shows as failed. See
  // resolveOutboundLetterLink for when that override stops applying.
  const getLink = (report) => resolveOutboundLetterLink(report, linkOverrides[`${caId}:${report?.pmsFileId}`]);

  // The share tokens the rows point at. The workflow's secure files are only
  // fetched when there is at least one, and fetched again when they change.
  const linkSignature = canUseSecureLinks
    ? _.uniq(
        sortedReports
          .map(getLink)
          .filter((link) => link.secureFileStatus === OUTBOUND_LETTER_SECURE_FILE_STATUS.READY && link.secureFileToken)
          .map((link) => link.secureFileToken)
      )
        .sort()
        .join(",")
    : "";

  useEffect(() => {
    if (!caId || !linkSignature) {
      // Nothing to load any more, so an earlier load failure no longer applies.
      setPanelError((current) => (current === LOAD_ERROR_MESSAGE ? null : current));
      return undefined;
    }
    let isCurrent = true;
    const loadSecureFiles = async () => {
      const { data, error } = await listSecureFiles(caId);
      if (!isCurrent) return;
      if (error) {
        setPanelError(LOAD_ERROR_MESSAGE);
        return;
      }
      setSecureFiles({ caId, signature: linkSignature, byToken: _.keyBy(data || [], "shareToken") });
      // A later successful load clears its own earlier failure, but not an
      // unrelated mark / copy / retry message.
      setPanelError((current) => (current === LOAD_ERROR_MESSAGE ? null : current));
    };
    loadSecureFiles();
    return () => {
      isCurrent = false;
    };
  }, [caId, linkSignature]);

  // Only trust a list fetched for this workflow and these tokens.
  const secureFilesByToken =
    secureFiles.caId === caId && secureFiles.signature === linkSignature ? secureFiles.byToken : null;

  const handleCopyLink = (reportKey, shareToken) => async () => {
    setPanelError(null);
    try {
      await navigator.clipboard.writeText(buildShareUrl(shareToken));
      clearTimeout(copiedTimerRef.current);
      setCopiedKey(reportKey);
      copiedTimerRef.current = setTimeout(() => setCopiedKey(null), COPIED_DURATION_MS);
    } catch (err) {
      setPanelError("Unable to copy link to clipboard.");
    }
  };

  const handleRetry = (report) => async () => {
    const pmsFileId = report?.pmsFileId;
    if (!pmsFileId || !caId || retryingKeys[pmsFileId]) return;

    setRetryingKeys((current) => ({ ...current, [pmsFileId]: true }));
    setPanelError(null);
    try {
      const { data, error } = await retryOutboundLetterSecureFile({ clientAppointmentId: caId, pmsFileId });
      if (error || !_.get(data, "secureFileToken")) {
        setPanelError(RETRY_ERROR_MESSAGE);
        return;
      }
      setLinkOverrides((current) => ({
        ...current,
        [`${caId}:${pmsFileId}`]: {
          secureFileToken: data.secureFileToken,
          secureFileStatus: _.get(data, "secureFileStatus", OUTBOUND_LETTER_SECURE_FILE_STATUS.READY),
          // Applies only while the entry still shows the failure retried here.
          basisTime: _.get(report, "time", null) || null,
        },
      }));
    } catch (err) {
      setPanelError(RETRY_ERROR_MESSAGE);
    } finally {
      setRetryingKeys((current) => _.omit(current, pmsFileId));
    }
  };

  const handleDownload = (report) => async () => {
    const key = report.pmsFileId || report.id;
    if (!key) return;
    setDownloadingKey(key);
    try {
      await triggerGPReportDownload(report);
    } catch (err) {
      console.error("Error downloading outbound letter", err);
    } finally {
      setDownloadingKey(null);
    }
  };

  const handleMark = (report) => async () => {
    const pmsFileId = report?.pmsFileId;
    if (!pmsFileId || !ca?.id) return;
    // Click on already-marked row is a no-op (button stays vibrant rather than
    // disabled-grey, but the action is suppressed here).
    if (pmsFileId === markedPmsFileId) return;
    // While another mark request is in flight, ignore subsequent clicks to
    // avoid races.
    if (markingKey) return;

    const previousMarked = markedPmsFileId;
    setMarkedPmsFileId(pmsFileId); // optimistic
    setMarkingKey(pmsFileId);
    setPanelError(null);
    try {
      await markGPReportService({ caId: ca.id, pmsFileId });
    } catch (err) {
      console.error("Error marking outbound letter as GP report", err);
      setMarkedPmsFileId(previousMarked); // rollback
      setPanelError("Could not mark as GP report. Please try again.");
    } finally {
      setMarkingKey(null);
    }
  };

  if (_.isEmpty(sortedReports)) {
    return (
      <div className="gp-report-panel has-background-white" style={PANEL_STYLE}>
        <div style={HEADER_STYLE} className="is-flex is-align-items-center is-justify-content-space-between">
          <span className="has-text-weight-semibold has-text-grey-dark is-size-7">Outbound Letters</span>
          <span className="tag is-rounded is-small">0</span>
        </div>
        <div className="has-text-grey is-size-7" style={{ padding: "16px 14px", background: "#fff" }}>
          No outbound letters for this patient yet.
        </div>
      </div>
    );
  }

  return (
    <div className="gp-report-panel has-background-white" style={PANEL_STYLE}>
      <div style={HEADER_STYLE} className="is-flex is-align-items-center is-justify-content-space-between">
        <span className="has-text-weight-semibold has-text-grey-dark is-size-7">Outbound Letters</span>
        <span className="tag is-rounded is-small">{sortedReports.length}</span>
      </div>

      {panelError && (
        <div
          className="has-text-danger is-size-7"
          style={{ padding: "8px 14px", background: "#fff5f5", borderBottom: "1px solid #f0d0d0" }}
        >
          {panelError}
        </div>
      )}

      <div>
        {sortedReports.map((report, idx) => {
          const reportKey = getReportKey(report);
          const rowKey = reportKey || `${report.name || "report"}-${idx}`;
          const isDownloading = downloadingKey === reportKey;
          const canDownload = Boolean(reportKey);
          const formattedTime = formatLocalTime(report.time || report.appointment_date) || "—";
          const isLast = idx === sortedReports.length - 1;

          const canMark = Boolean(report.pmsFileId);
          const isMarked = canMark && report.pmsFileId === markedPmsFileId;
          const isMarking = markingKey === report.pmsFileId;
          const otherMarkInFlight = Boolean(markingKey) && !isMarking;

          const rowStyle = {
            ...ROW_STYLE,
            ...(isLast ? { borderBottom: "none" } : null),
            ...(isMarked ? MARKED_ROW_OVERRIDE : null),
          };

          // Mark button visual:
          //  - marked     → solid green pill with check + "GP Report" (NOT
          //                 disabled, so styling stays vibrant; click is a
          //                 no-op via handleMark)
          //  - in-flight  → loading spinner overlay while the API resolves
          //  - unmarked   → outlined info pill with "Set as GP Report"
          //  - cannot     → outlined pill, disabled (no pmsFileId)
          const markButtonClass = (() => {
            const base = "button is-small is-radiusless";
            if (isMarking) return `${base} is-success is-loading`;
            if (isMarked) return `${base} is-success`;
            return `${base} is-info is-outlined`;
          })();
          const link = getLink(report);
          const linkState = canUseSecureLinks
            ? getOutboundLetterLinkState(link, secureFilesByToken)
            : OUTBOUND_LETTER_LINK_STATE.NONE;
          const isCopied = copiedKey === reportKey;
          const isRetrying = Boolean(retryingKeys[report.pmsFileId]);

          const markButtonTitle = !canMark
            ? "This entry has no PMS file id and cannot be marked."
            : isMarked
            ? "Currently marked as the GP report. Click another row to change."
            : "Set this letter as the GP report. Used by the Manage Tasks auto-send email.";

          return (
            <div key={rowKey} className="gp-report-row" style={rowStyle}>
              <div className="is-size-7 is-family-monospace" style={TIME_COL_STYLE} title={formattedTime}>
                {formattedTime}
              </div>
              <div
                className="is-size-7 has-text-weight-medium has-text-grey-dark"
                style={NAME_COL_STYLE}
                title={report.name}
              >
                {report.name || "(unnamed letter)"}
              </div>

              <div style={ACTIONS_COL_STYLE}>
                {linkState === OUTBOUND_LETTER_LINK_STATE.ACTIVE && (
                  <button
                    type="button"
                    className={`button is-small is-radiusless ${isCopied ? "is-success" : "is-info is-outlined"}`}
                    style={COPY_BUTTON_STYLE}
                    onClick={handleCopyLink(reportKey, link.secureFileToken)}
                    title="Copy the secure link to paste into the patient's email"
                  >
                    {/* Separate keyed icons: Font Awesome's JS replaces the <i>
                        with an <svg>, so a className change would not show. */}
                    {isCopied ? (
                      <span key="copied" className="icon is-small">
                        <i className="fas fa-check" />
                      </span>
                    ) : (
                      <span key="copy" className="icon is-small">
                        <i className="fas fa-link" />
                      </span>
                    )}
                    <span aria-live="polite">{isCopied ? "Copied" : "Copy secure link"}</span>
                  </button>
                )}

                {linkState === OUTBOUND_LETTER_LINK_STATE.FAILED && (
                  <>
                    <span className="is-size-7 has-text-grey" style={LINK_STATUS_TEXT_STYLE}>
                      Secure link failed
                    </span>
                    <button
                      type="button"
                      className={`button is-small is-light is-radiusless${isRetrying ? " is-loading" : ""}`}
                      onClick={handleRetry(report)}
                      disabled={isRetrying}
                      title="Try creating the secure link again"
                    >
                      Retry
                    </button>
                  </>
                )}

                {linkState === OUTBOUND_LETTER_LINK_STATE.EXPIRED && (
                  <span className="is-size-7 has-text-grey" style={LINK_STATUS_TEXT_STYLE}>
                    Secure link expired
                  </span>
                )}

                <button
                  type="button"
                  className={markButtonClass}
                  style={MARK_BUTTON_BASE_STYLE}
                  onClick={handleMark(report)}
                  disabled={!canMark || otherMarkInFlight}
                  title={markButtonTitle}
                >
                  {isMarked && (
                    <span className="icon is-small">
                      <i className="fas fa-check-circle" />
                    </span>
                  )}
                  <span>{isMarked ? "GP Report" : "Set as GP Report"}</span>
                </button>

                <button
                  type="button"
                  className={`button is-small is-info is-outlined is-radiusless${isDownloading ? " is-loading" : ""}`}
                  onClick={handleDownload(report)}
                  disabled={!canDownload || isDownloading}
                  title={canDownload ? "Download letter" : "No file available"}
                >
                  <span className="icon is-small">
                    <i className="fas fa-download" />
                  </span>
                  <span>Download</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GPReportListPanel;
